/**
 * useNewActionAnimation — tracks which proposals just arrived (via WebSocket
 * or refetch) so their rows can play the entrance animation.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { ProposalListItem } from '@/lib/proposals/core/types';

export function useNewActionAnimation(
  results: ProposalListItem[] | undefined
): Set<number> {
  // Track new actions for entrance animation.
  // useLayoutEffect fires synchronously before paint, so the animation
  // class is present on the first paint (no flash). State persists until
  // explicitly cleared, so re-renders don't strip the class mid-animation.
  const knownActionIdsRef = useRef<Set<number> | null>(null);
  const [newActionIds, setNewActionIds] = useState<Set<number>>(new Set());

  useLayoutEffect(() => {
    if (!results) {
      return;
    }

    const currentIds = new Set(results.map(r => r.id));

    if (knownActionIdsRef.current === null) {
      // First load — seed known IDs, no animation
      knownActionIdsRef.current = currentIds;
      return;
    }

    const freshIds = new Set<number>();
    for (const id of currentIds) {
      if (!knownActionIdsRef.current.has(id)) {
        freshIds.add(id);
      }
    }

    knownActionIdsRef.current = currentIds;

    if (freshIds.size > 0) {
      setNewActionIds(freshIds);
    }
  }, [results]);

  // Clear animation classes after the animation completes
  useEffect(() => {
    if (newActionIds.size === 0) {
      return;
    }
    const timer = setTimeout(() => {
      setNewActionIds(new Set());
    }, 800);
    return () => clearTimeout(timer);
  }, [newActionIds]);

  return newActionIds;
}
